import { useEffect, useState, useCallback } from "react";
import {
  api,
  Roadmap,
  Module,
  PracticeOverviewStudent,
  PracticeDetailDay,
} from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Plus,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Pause,
  Trash2,
  Eye,
  RotateCcw,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Loader2,
} from "lucide-react";

interface CurriculumTabProps {
  classroomId: string;
  classroomName: string;
}

// Derived module assignment info from practice overview
interface DerivedModuleAssignment {
  assignment_id: string;
  module_id: string;
  module_name: string;
  roadmap_name: string;
  practice_days_required: number;
  students_completed: number;
  students_total: number;
  students: {
    student_id: string;
    username: string;
    first_name?: string;
    last_name?: string;
    practice_days_completed: number;
    practice_days_required: number;
    days_remaining: number;
    status: string;
  }[];
}

// Module config for the assign modal
interface ModuleConfig {
  module_id: string;
  roadmap_id: string;
  module_name: string;
  roadmap_name: string;
  technique_ids: string[];
  practice_days_required: number;
  window_days: number;
  challenges_count_as_practice: boolean;
}

export default function CurriculumTab({
  classroomId,
  classroomName,
}: CurriculumTabProps) {
  // Practice overview data
  const [_overviewStudents, setOverviewStudents] = useState<
    PracticeOverviewStudent[]
  >([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [overviewError, setOverviewError] = useState("");

  // Derived module assignments
  const [moduleAssignments, setModuleAssignments] = useState<
    DerivedModuleAssignment[]
  >([]);

  // Expanded module row
  const [expandedModule, setExpandedModule] = useState<string | null>(null);

  // Assign modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [roadmaps, setRoadmaps] = useState<Roadmap[]>([]);
  const [loadingRoadmaps, setLoadingRoadmaps] = useState(false);
  const [selectedModules, setSelectedModules] = useState<ModuleConfig[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState("");

  // Extend window dialog
  const [extendDialog, setExtendDialog] = useState<{
    open: boolean;
    assignmentId: string;
    studentId: string;
    studentName: string;
    extendDays: number;
  }>({ open: false, assignmentId: "", studentId: "", studentName: "", extendDays: 3 });

  // Reset dialog
  const [resetDialog, setResetDialog] = useState<{
    open: boolean;
    assignmentId: string;
    studentId: string;
    studentName: string;
  }>({ open: false, assignmentId: "", studentId: "", studentName: "" });

  // Practice detail (heatmap) dialog
  const [heatmapDialog, setHeatmapDialog] = useState<{
    open: boolean;
    studentId: string;
    studentName: string;
    assignmentId: string;
    loading: boolean;
    days: PracticeDetailDay[];
  }>({
    open: false,
    studentId: "",
    studentName: "",
    assignmentId: "",
    loading: false,
    days: [],
  });

  // Remove/pause module
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadPracticeOverview = useCallback(async () => {
    try {
      setLoadingOverview(true);
      setOverviewError("");
      const result = await api.getClassroomPracticeOverview(classroomId);
      setOverviewStudents(result.students);
      deriveModuleAssignments(result.students);
    } catch (error) {
      console.error("Failed to load practice overview:", error);
      setOverviewError(
        error instanceof Error ? error.message : "Failed to load practice data"
      );
    } finally {
      setLoadingOverview(false);
    }
  }, [classroomId]);

  useEffect(() => {
    loadPracticeOverview();
  }, [loadPracticeOverview]);

  // Derive unique module assignments from student data
  const deriveModuleAssignments = (students: PracticeOverviewStudent[]) => {
    const moduleMap = new Map<string, DerivedModuleAssignment>();

    for (const student of students) {
      for (const mod of student.modules) {
        const key = mod.assignment_id;
        if (!moduleMap.has(key)) {
          moduleMap.set(key, {
            assignment_id: mod.assignment_id,
            module_id: mod.module_id,
            module_name: mod.module_name ?? mod.module_id,
            roadmap_name: mod.roadmap_name ?? mod.roadmap_id ?? "",
            practice_days_required: mod.practice_days_required,
            students_completed: 0,
            students_total: 0,
            students: [],
          });
        }
        const entry = moduleMap.get(key)!;
        entry.students_total++;
        if (mod.status === "completed") {
          entry.students_completed++;
        }
        entry.students.push({
          student_id: student.student_id,
          username: student.username,
          first_name: student.first_name,
          last_name: student.last_name,
          practice_days_completed: mod.practice_days_completed,
          practice_days_required: mod.practice_days_required,
          days_remaining: mod.days_remaining,
          status: mod.status,
        });
      }
    }

    setModuleAssignments(Array.from(moduleMap.values()));
  };

  const loadRoadmaps = async () => {
    try {
      setLoadingRoadmaps(true);
      const data = await api.getRoadmaps(true);
      setRoadmaps(data);
    } catch (error) {
      console.error("Failed to load roadmaps:", error);
    } finally {
      setLoadingRoadmaps(false);
    }
  };

  const handleOpenAssignModal = () => {
    setAssignModalOpen(true);
    setSelectedModules([]);
    setAssignError("");
    if (roadmaps.length === 0) {
      loadRoadmaps();
    }
  };

  const toggleModuleSelection = (
    mod: Module,
    roadmap: Roadmap
  ) => {
    const existing = selectedModules.find((m) => m.module_id === mod.id);
    if (existing) {
      setSelectedModules(selectedModules.filter((m) => m.module_id !== mod.id));
    } else {
      // Extract technique_ids from lessons
      const techniqueIds = (mod.lessons || [])
        .map((l) => l.technique_id)
        .filter(Boolean);
      setSelectedModules([
        ...selectedModules,
        {
          module_id: mod.id,
          roadmap_id: roadmap.id,
          module_name: mod.name,
          roadmap_name: roadmap.name,
          technique_ids: techniqueIds,
          practice_days_required: 5,
          window_days: 10,
          challenges_count_as_practice: true,
        },
      ]);
    }
  };

  const updateModuleConfig = (
    moduleId: string,
    field: keyof ModuleConfig,
    value: any
  ) => {
    setSelectedModules(
      selectedModules.map((m) =>
        m.module_id === moduleId ? { ...m, [field]: value } : m
      )
    );
  };

  const handleAssignModules = async () => {
    if (selectedModules.length === 0) {
      setAssignError("Please select at least one module to assign");
      return;
    }

    try {
      setAssigning(true);
      setAssignError("");
      await api.assignModulesToClassroom({
        classroom_id: classroomId,
        modules: selectedModules.map((m) => ({
          module_id: m.module_id,
          roadmap_id: m.roadmap_id,
          technique_ids: m.technique_ids,
          practice_days_required: m.practice_days_required,
          window_days: m.window_days,
          challenges_count_as_practice: m.challenges_count_as_practice,
        })),
      });
      setAssignModalOpen(false);
      setSelectedModules([]);
      await loadPracticeOverview();
    } catch (error) {
      setAssignError(
        error instanceof Error ? error.message : "Failed to assign modules"
      );
    } finally {
      setAssigning(false);
    }
  };

  const handlePauseModule = async (assignmentId: string) => {
    try {
      setActionLoading(assignmentId);
      await api.removeModuleAssignment(assignmentId, "pause");
      await loadPracticeOverview();
    } catch (error) {
      alert(
        error instanceof Error ? error.message : "Failed to pause module"
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveModule = async (assignmentId: string) => {
    if (
      !confirm("Remove this module assignment? This action cannot be undone.")
    ) {
      return;
    }
    try {
      setActionLoading(assignmentId);
      await api.removeModuleAssignment(assignmentId, "remove");
      await loadPracticeOverview();
    } catch (error) {
      alert(
        error instanceof Error ? error.message : "Failed to remove module"
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleExtendWindow = async () => {
    try {
      await api.updateModuleAssignment({
        assignment_id: extendDialog.assignmentId,
        student_id: extendDialog.studentId,
        updates: { extend_window_days: extendDialog.extendDays },
      });
      setExtendDialog({ ...extendDialog, open: false });
      await loadPracticeOverview();
    } catch (error) {
      alert(
        error instanceof Error ? error.message : "Failed to extend window"
      );
    }
  };

  const handleResetProgress = async () => {
    try {
      await api.updateModuleAssignment({
        assignment_id: resetDialog.assignmentId,
        student_id: resetDialog.studentId,
        updates: { reset_progress: true },
      });
      setResetDialog({ ...resetDialog, open: false });
      await loadPracticeOverview();
    } catch (error) {
      alert(
        error instanceof Error ? error.message : "Failed to reset progress"
      );
    }
  };

  const handleViewPracticeDetail = async (
    studentId: string,
    studentName: string,
    assignmentId: string
  ) => {
    setHeatmapDialog({
      open: true,
      studentId,
      studentName,
      assignmentId,
      loading: true,
      days: [],
    });

    try {
      const result = await api.getStudentPracticeDetail(
        studentId,
        classroomId,
        assignmentId
      );
      const assignmentData = result.assignments?.find(
        (a) => a.assignment_id === assignmentId
      );
      setHeatmapDialog((prev) => ({
        ...prev,
        loading: false,
        days: assignmentData?.practice_days || [],
      }));
    } catch (error) {
      console.error("Failed to load practice detail:", error);
      setHeatmapDialog((prev) => ({
        ...prev,
        loading: false,
        days: [],
      }));
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </Badge>
        );
      case "in_progress":
        return (
          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
            <Clock className="w-3 h-3 mr-1" />
            In Progress
          </Badge>
        );
      case "at_risk":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            <AlertCircle className="w-3 h-3 mr-1" />
            At Risk
          </Badge>
        );
      case "missed":
        return (
          <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
            <XCircle className="w-3 h-3 mr-1" />
            Missed
          </Badge>
        );
      case "not_started":
        return (
          <Badge variant="secondary">
            Not Started
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getStudentDisplayName = (student: {
    username: string;
    first_name?: string;
    last_name?: string;
  }) => {
    if (student.first_name || student.last_name) {
      return `${student.first_name || ""} ${student.last_name || ""}`.trim();
    }
    return student.username;
  };

  // Determine student status for display purposes
  const getEffectiveStatus = (student: {
    status: string;
    days_remaining: number;
    practice_days_completed: number;
    practice_days_required: number;
  }) => {
    if (student.status === "completed") return "completed";
    if (student.status === "missed") return "missed";
    if (student.status === "not_started") return "not_started";
    // In progress: check if at risk
    if (
      student.status === "in_progress" &&
      student.days_remaining <= 2 &&
      student.practice_days_completed < student.practice_days_required
    ) {
      return "at_risk";
    }
    return student.status;
  };

  if (loadingOverview) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-500">Loading curriculum data...</p>
        </CardContent>
      </Card>
    );
  }

  if (overviewError) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
          <p className="text-sm text-red-600">{overviewError}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={loadPracticeOverview}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Assigned Modules
              </CardTitle>
              <CardDescription>
                Manage curriculum modules and track student practice progress
              </CardDescription>
            </div>
            <Button onClick={handleOpenAssignModal}>
              <Plus className="w-4 h-4 mr-2" />
              Assign Module
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {moduleAssignments.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50 text-gray-400" />
              <p className="text-sm text-gray-500 mb-2">
                No modules assigned to this classroom yet
              </p>
              <p className="text-xs text-gray-400 mb-4">
                Assign curriculum modules to track student practice progress
              </p>
              <Button onClick={handleOpenAssignModal}>
                <Plus className="w-4 h-4 mr-2" />
                Assign Your First Module
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {moduleAssignments.map((assignment) => (
                <div key={assignment.assignment_id} className="border rounded-lg">
                  {/* Module Row */}
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    onClick={() =>
                      setExpandedModule(
                        expandedModule === assignment.assignment_id
                          ? null
                          : assignment.assignment_id
                      )
                    }
                  >
                    <div className="flex items-center gap-3">
                      {expandedModule === assignment.assignment_id ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                      <div>
                        <p className="font-medium">
                          {assignment.module_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {assignment.roadmap_name} &middot; {assignment.practice_days_required} days practice required
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <Progress
                            value={
                              assignment.students_total > 0
                                ? (assignment.students_completed /
                                    assignment.students_total) *
                                  100
                                : 0
                            }
                            className="w-24 h-2"
                          />
                          <span className="text-sm font-medium min-w-[60px] text-right">
                            {assignment.students_completed}/
                            {assignment.students_total}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          students completed
                        </p>
                      </div>
                      <div
                        className="flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            handlePauseModule(assignment.assignment_id)
                          }
                          disabled={actionLoading === assignment.assignment_id}
                          title="Pause module"
                        >
                          {actionLoading === assignment.assignment_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Pause className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() =>
                            handleRemoveModule(assignment.assignment_id)
                          }
                          disabled={actionLoading === assignment.assignment_id}
                          title="Remove module"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Student Progress */}
                  {expandedModule === assignment.assignment_id && (
                    <div className="border-t px-4 pb-4">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Student</TableHead>
                            <TableHead>Days Practiced</TableHead>
                            <TableHead>Days Left</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {assignment.students.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={5}
                                className="text-center text-gray-500 py-4"
                              >
                                No students enrolled
                              </TableCell>
                            </TableRow>
                          ) : (
                            assignment.students.map((student) => {
                              const effectiveStatus = getEffectiveStatus(student);
                              return (
                                <TableRow key={student.student_id}>
                                  <TableCell className="font-medium">
                                    {getStudentDisplayName(student)}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Progress
                                        value={
                                          student.practice_days_required > 0
                                            ? (student.practice_days_completed /
                                                student.practice_days_required) *
                                              100
                                            : 0
                                        }
                                        className="w-16 h-2"
                                      />
                                      <span className="text-sm">
                                        {student.practice_days_completed}/
                                        {student.practice_days_required}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {student.days_remaining >= 0 ? (
                                      <span
                                        className={
                                          student.days_remaining <= 2
                                            ? "text-orange-600 font-medium"
                                            : "text-gray-600"
                                        }
                                      >
                                        {student.days_remaining} days
                                      </span>
                                    ) : (
                                      <span className="text-red-600 font-medium">
                                        Expired
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {getStatusBadge(effectiveStatus)}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        title="View practice detail"
                                        onClick={() =>
                                          handleViewPracticeDetail(
                                            student.student_id,
                                            getStudentDisplayName(student),
                                            assignment.assignment_id
                                          )
                                        }
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      {(effectiveStatus === "at_risk" ||
                                        effectiveStatus === "in_progress" ||
                                        effectiveStatus === "missed") && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          title="Extend window"
                                          onClick={() =>
                                            setExtendDialog({
                                              open: true,
                                              assignmentId:
                                                assignment.assignment_id,
                                              studentId: student.student_id,
                                              studentName:
                                                getStudentDisplayName(student),
                                              extendDays: 3,
                                            })
                                          }
                                        >
                                          <Clock className="w-4 h-4" />
                                        </Button>
                                      )}
                                      {effectiveStatus === "missed" && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          title="Reset progress"
                                          className="text-orange-600 hover:text-orange-700"
                                          onClick={() =>
                                            setResetDialog({
                                              open: true,
                                              assignmentId:
                                                assignment.assignment_id,
                                              studentId: student.student_id,
                                              studentName:
                                                getStudentDisplayName(student),
                                            })
                                          }
                                        >
                                          <RotateCcw className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== Assign Module Modal ===== */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Modules to {classroomName}</DialogTitle>
            <DialogDescription>
              Select modules from the curriculum to assign to your classroom.
              Students will need to practice the techniques in each module.
            </DialogDescription>
          </DialogHeader>

          {loadingRoadmaps ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">Loading curriculum...</p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Roadmap / Module Selection */}
              <div className="border rounded-lg">
                <Accordion type="multiple">
                  {roadmaps.map((roadmap) => (
                    <AccordionItem
                      key={roadmap.id}
                      value={roadmap.id}
                    >
                      <AccordionTrigger className="px-4">
                        <div className="flex items-center gap-2 text-left">
                          <span className="font-medium">{roadmap.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {roadmap.modules?.length || 0} modules
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4">
                        <div className="space-y-2">
                          {(roadmap.modules || []).map((mod) => {
                            const isSelected = selectedModules.some(
                              (s) => s.module_id === mod.id
                            );
                            return (
                              <div
                                key={mod.id}
                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                  isSelected
                                    ? "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800"
                                    : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                                }`}
                                onClick={() =>
                                  toggleModuleSelection(mod, roadmap)
                                }
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() =>
                                    toggleModuleSelection(mod, roadmap)
                                  }
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-sm">
                                    {mod.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {mod.description?.slice(0, 80) || "No description"}
                                    {(mod.description?.length || 0) > 80
                                      ? "..."
                                      : ""}
                                  </p>
                                  {mod.lessons && mod.lessons.length > 0 && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      {mod.lessons.length} lessons
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {(!roadmap.modules || roadmap.modules.length === 0) && (
                            <p className="text-sm text-gray-500 py-2">
                              No modules available in this roadmap
                            </p>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Selected Module Configuration */}
              {selectedModules.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">
                    Configure Selected Modules ({selectedModules.length})
                  </h4>
                  {selectedModules.map((config) => (
                    <Card key={config.module_id}>
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium text-sm">
                              {config.module_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {config.roadmap_name}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500"
                            onClick={() =>
                              setSelectedModules(
                                selectedModules.filter(
                                  (m) => m.module_id !== config.module_id
                                )
                              )
                            }
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Practice Days</Label>
                            <Input
                              type="number"
                              min={1}
                              max={30}
                              value={config.practice_days_required}
                              onChange={(e) =>
                                updateModuleConfig(
                                  config.module_id,
                                  "practice_days_required",
                                  parseInt(e.target.value) || 5
                                )
                              }
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Window (days)</Label>
                            <Input
                              type="number"
                              min={1}
                              max={90}
                              value={config.window_days}
                              onChange={(e) =>
                                updateModuleConfig(
                                  config.module_id,
                                  "window_days",
                                  parseInt(e.target.value) || 10
                                )
                              }
                              className="h-8"
                            />
                          </div>
                          <div className="flex items-end gap-2 pb-1">
                            <Switch
                              checked={config.challenges_count_as_practice}
                              onCheckedChange={(checked) =>
                                updateModuleConfig(
                                  config.module_id,
                                  "challenges_count_as_practice",
                                  checked
                                )
                              }
                            />
                            <Label className="text-xs">Challenges count</Label>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {assignError && (
                <p className="text-sm text-red-600">{assignError}</p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAssignModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignModules}
              disabled={assigning || selectedModules.length === 0}
            >
              {assigning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                `Assign ${selectedModules.length} Module${
                  selectedModules.length !== 1 ? "s" : ""
                }`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Extend Window Dialog ===== */}
      <Dialog
        open={extendDialog.open}
        onOpenChange={(open) => setExtendDialog({ ...extendDialog, open })}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Extend Practice Window</DialogTitle>
            <DialogDescription>
              Add extra days for {extendDialog.studentName} to complete their
              practice requirement.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Additional Days</Label>
            <Input
              type="number"
              min={1}
              max={30}
              value={extendDialog.extendDays}
              onChange={(e) =>
                setExtendDialog({
                  ...extendDialog,
                  extendDays: parseInt(e.target.value) || 1,
                })
              }
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setExtendDialog({ ...extendDialog, open: false })
              }
            >
              Cancel
            </Button>
            <Button onClick={handleExtendWindow}>
              Extend by {extendDialog.extendDays} Day
              {extendDialog.extendDays !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Reset Progress Dialog ===== */}
      <Dialog
        open={resetDialog.open}
        onOpenChange={(open) => setResetDialog({ ...resetDialog, open })}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Reset Student Progress</DialogTitle>
            <DialogDescription>
              Reset all practice progress for {resetDialog.studentName}. They
              will need to start over from scratch.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-orange-600">
              This will clear all recorded practice days and restart the
              practice window. This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setResetDialog({ ...resetDialog, open: false })
              }
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleResetProgress}>
              Reset Progress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Practice Heatmap Dialog ===== */}
      <Dialog
        open={heatmapDialog.open}
        onOpenChange={(open) =>
          setHeatmapDialog({ ...heatmapDialog, open })
        }
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Practice Detail - {heatmapDialog.studentName}
            </DialogTitle>
            <DialogDescription>
              Daily practice activity for the assigned module
            </DialogDescription>
          </DialogHeader>

          {heatmapDialog.loading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                Loading practice detail...
              </p>
            </div>
          ) : heatmapDialog.days.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-500">
                No practice data available yet
              </p>
            </div>
          ) : (
            <div className="py-4 space-y-4">
              {/* Heatmap Grid */}
              <div className="space-y-1">
                <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-green-500" />
                    <span>Practiced</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-gray-200 dark:bg-gray-700" />
                    <span>Not practiced</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {heatmapDialog.days.map((day) => (
                    <div
                      key={day.date}
                      className={`w-8 h-8 rounded flex items-center justify-center text-xs font-medium cursor-default ${
                        day.practiced
                          ? "bg-green-500 text-white"
                          : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                      }`}
                      title={`${day.date}${
                        day.practiced
                          ? ` - ${day.problems_attempted || 0} problems`
                          : " - No practice"
                      }`}
                    >
                      {new Date(day.date).getDate()}
                    </div>
                  ))}
                </div>
              </div>

              {/* Detail Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Techniques</TableHead>
                      <TableHead>Problems</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {heatmapDialog.days.map((day) => (
                      <TableRow key={day.date}>
                        <TableCell className="text-sm">
                          {new Date(day.date).toLocaleDateString(undefined, {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                          })}
                        </TableCell>
                        <TableCell>
                          {day.practiced ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-gray-400" />
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {day.techniques_practiced
                            ?.map(
                              (t) =>
                                `${t.name} (${t.accuracy}%)`
                            )
                            .join(", ") || "-"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {day.problems_attempted || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setHeatmapDialog({ ...heatmapDialog, open: false })
              }
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
